import { useState } from 'react';
import { api } from '@libero/core/services/api';

interface TagCount {
  tagName: string;
  orderCount: number;
  articleCount: number;
  percentage: number;
  articles: ArticleClaimCount[];
}

interface ArticleClaimCount {
  articleName: string;
  claimCount: number;
}

interface AnalysisResult {
  totalOrders: number;
  tags: TagCount[];
  articles: ArticleClaimCount[];
  dateFrom: string;
  dateTo: string;
}

export default function AnalyseGLApp() {
  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);

  const canStart = dateFrom && dateTo && dateFrom <= dateTo;

  async function handleStart() {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await api.get<{ success: boolean; data: AnalysisResult; error?: { message?: string } }>(
        `/programmes/analyse-gl/tag-analysis?dateFrom=${dateFrom}&dateTo=${dateTo}`,
      );

      if (!res.data.success) {
        setError(res.data.error?.message ?? 'Unbekannter Fehler');
        return;
      }

      setResult(res.data.data);
    } catch (e: any) {
      setError('Verbindungsfehler: ' + (e?.response?.data?.error?.message ?? e?.message ?? 'Unbekannt'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-[#1A1A1A] mb-1">Analyse Gewährleistungen</h1>
      <p className="text-sm text-gray-500 mb-6">Wieviele Aufträge haben welchen Gewährleistungs-Tag?</p>

      {/* Zeitraum-Eingabe */}
      <div className="flex items-end gap-4 mb-6">
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Von</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-[#DAD9D9] rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E9FF57] focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Bis</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-[#DAD9D9] rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E9FF57] focus:border-transparent"
          />
        </div>
        <button
          onClick={handleStart}
          disabled={!canStart || loading}
          className="px-5 py-2 rounded font-semibold text-sm bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Lädt...' : 'Analyse starten'}
        </button>
      </div>

      {/* Fehler */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Ladeindikator */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-4 h-4 border-2 border-[#E9FF57] border-t-[#1A1A1A] rounded-full animate-spin" />
          Aufträge werden geladen...
        </div>
      )}

      {/* Ergebnis */}
      {result && (
        <div>
          <p className="text-sm text-gray-600 mb-3">
            <span className="font-semibold text-[#1A1A1A]">{result.totalOrders.toLocaleString('de-DE')}</span> Aufträge mit GL-Tags gefunden
            {' '}({result.dateFrom} bis {result.dateTo})
          </p>

          <div className="border border-[#DAD9D9] rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A1A1A] text-white">
                  <th className="text-left px-4 py-3 font-semibold">Tag</th>
                  <th className="text-right px-4 py-3 font-semibold">Aufträge</th>
                  <th className="text-right px-4 py-3 font-semibold">Artikel</th>
                  <th className="text-right px-4 py-3 font-semibold">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {result.tags.map((tag, i) => (
                  <tr key={tag.tagName} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2.5 text-[#1A1A1A]">{tag.tagName}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-[#1A1A1A]">
                      {tag.orderCount.toLocaleString('de-DE')}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-[#1A1A1A]">
                      {tag.articleCount.toLocaleString('de-DE')}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600">
                      {tag.percentage.toFixed(1).replace('.', ',')} %
                    </td>
                  </tr>
                ))}
                {result.tags.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                      Keine Aufträge im gewählten Zeitraum gefunden.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {result.articles.length > 0 && (
            <div className="mt-8">
              <h2 className="text-base font-semibold text-[#1A1A1A] mb-3">Betroffene Artikel</h2>

              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  onClick={() => setSelectedTagFilter(null)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    selectedTagFilter === null
                      ? 'bg-[#1A1A1A] text-white'
                      : 'bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#DAD9D9]'
                  }`}
                >
                  Alle Tags
                </button>
                {result.tags.map(tag => (
                  <button
                    key={tag.tagName}
                    onClick={() => setSelectedTagFilter(tag.tagName)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      selectedTagFilter === tag.tagName
                        ? 'bg-[#1A1A1A] text-white'
                        : 'bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#DAD9D9]'
                    }`}
                  >
                    {tag.tagName}
                  </button>
                ))}
              </div>

              <div className="border border-[#DAD9D9] rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1A1A1A] text-white">
                      <th className="text-left px-4 py-3 font-semibold">Artikelname</th>
                      <th className="text-right px-4 py-3 font-semibold">Menge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedTagFilter === null
                      ? result.articles
                      : result.tags.find(t => t.tagName === selectedTagFilter)?.articles ?? []
                    ).map((article, i) => (
                      <tr key={article.articleName} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2.5 text-[#1A1A1A]">{article.articleName}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-[#1A1A1A]">
                          {article.claimCount.toLocaleString('de-DE')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
